import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Rocket, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import ProjectCard from '../components/ProjectCard';
import SEO from '../components/SEO';
import api from '../utils/api';

const Projects = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get('/projects?active_only=true');
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-20 bg-bms-light" data-testid="projects-page">
      <SEO 
        title="Projeler"
        description="BrightMind Software tarafından geliştirilen projeler ve başarı hikayeleri."
        keywords="projeler, portfolio, yazılım projeleri, başarı hikayeleri"
      />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center mb-16">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-bms-text-light hover:text-bms-primary mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.pages.backToHome}
            </Link>
            <h1 className="text-3xl md:text-5xl font-bold text-bms-text mb-4">
              {t.projects.title}
            </h1>
            <p className="text-lg text-bms-text-light max-w-2xl mx-auto">
              {t.projects.subtitle}
            </p>
          </div>
        </ScrollReveal>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center text-bms-text-light">{t.pages.loading}</div>
        ) : projects.length === 0 ? (
          <ScrollReveal>
            <div className="max-w-2xl mx-auto">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-12 border border-gray-100 shadow-corporate text-center"
              >
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-blue-50 mb-6">
                  <Rocket className="w-8 h-8 text-bms-primary" />
                </div>

                {/* Main Message */}
                <h3 className="text-2xl font-bold text-bms-text mb-4">
                  {t.projects.comingSoonTitle}
                </h3>
                
                <p className="text-bms-text-light mb-6">
                  {t.projects.comingSoonMessage}
                </p>

                {/* Status Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 rounded-full text-sm text-green-700 mb-6">
                  <Clock className="w-4 h-4" />
                  <span>{t.projects.comingSoonBadge}</span>
                </div>

                {/* CTA */}
                <div>
                  <Link 
                    to="/iletisim"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg font-medium transition-all"
                  >
                    {t.projects.contactUs}
                  </Link>
                </div>
              </motion.div>
            </div>
          </ScrollReveal>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <ScrollReveal key={project.id || index} delay={index * 0.1}>
                <ProjectCard project={project} />
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
